import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import type { TableSearchState } from '@/entities/settings';

/** Параметры привязки позиции прокрутки к составу списка */
type Params = {
  /** Действующие фильтры выборки (включая выборку перехода) */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  /** Сохранённый основной поиск */
  search: TableSearchState | undefined;
  /** Активный режим поиска по содержимому */
  activeContentMode: string | null;
  /** Зафиксированное значение поиска по содержимому */
  committedContentValue: string;
  /** Ключ состава списка, к которому привязана сохранённая позиция */
  savedScrollKey: string | undefined;
  /** Сохранённый индекс первой видимой строки */
  savedScrollIndex: number | undefined;
  /** Записывает индекс прокрутки для текущего состава */
  onScrollIndexChange: (index: number, compositionKey: string) => void;
};

/** Ключ состава списка и привязанная к нему позиция прокрутки */
type Result = {
  /** Состав списка: его смена обесценивает выбор строк и сохранённую позицию */
  listCompositionKey: string;
  /** Индекс, с которого восстанавливать прокрутку; `undefined` — состав сменился */
  initialScrollIndex: number | undefined;
  onScrollIndexChange: (index: number) => void;
};

/**
 * Позиция прокрутки, привязанная к составу списка. Состав — то, какие строки в нём и в каком
 * порядке: фильтры (включая выборку), сортировка, поиск по атрибутам и поиск по содержимому.
 * Видимость колонок в состав не входит: она меняет набор полей строки, но не сами строки.
 *
 * Позиция восстанавливается только в том составе, в котором была снята. В чужом составе строки
 * с таким индексом может не быть вовсе (вход в короткую выборку после прокрутки длинного списка) —
 * грид тогда навсегда остаётся в состоянии «позиция не достигнута»: строк, которые он считает
 * видимыми, у него нет, и смена сортировки или фильтров не приводит ни к какому запросу.
 */
export const useListScrollPosition = ({
  filters,
  sorting,
  search,
  activeContentMode,
  committedContentValue,
  savedScrollKey,
  savedScrollIndex,
  onScrollIndexChange,
}: Params): Result => {
  const listCompositionKey = useMemo(
    () => JSON.stringify([filters, sorting, search ?? null, activeContentMode, committedContentValue]),
    [filters, sorting, search, activeContentMode, committedContentValue],
  );

  const initialScrollIndex = savedScrollKey === listCompositionKey ? savedScrollIndex : undefined;

  const handleScrollIndexChange = useCallback(
    (index: number) => {
      onScrollIndexChange(index, listCompositionKey);
    },
    [onScrollIndexChange, listCompositionKey],
  );

  return { listCompositionKey, initialScrollIndex, onScrollIndexChange: handleScrollIndexChange };
};
